import { useState } from "react";
import type { Category, EventId, Priority, Task, TaskId } from "../../../../shared/types";

interface EventLinkedTasksProps {
  readonly eventId: EventId;
  readonly eventCategory: Category;
  readonly eventDate: string;
  readonly tasks: readonly Task[];
  readonly onToggleTask: (id: TaskId) => void;
  readonly onAddTask: (
    title: string,
    category: Category,
    eventId: EventId,
    dueDate: string | null,
    priority: Priority,
  ) => void;
  readonly onUnlinkTask: (id: TaskId) => void;
  readonly onDeleteTask: (id: TaskId) => void;
  readonly readOnly?: boolean;
}

export function EventLinkedTasks({
  eventId,
  eventCategory,
  eventDate,
  tasks,
  onToggleTask,
  onAddTask,
  onUnlinkTask,
  onDeleteTask,
  readOnly,
}: EventLinkedTasksProps) {
  const [newTitle, setNewTitle] = useState("");
  const [menuOpenId, setMenuOpenId] = useState<TaskId | null>(null);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = newTitle.trim();
    if (!trimmed) return;
    onAddTask(trimmed, eventCategory, eventId, eventDate, "normal");
    setNewTitle("");
  }

  const doneCount = tasks.filter((t) => t.status === "done").length;

  return (
    <div className="space-y-2">
      {!readOnly && (
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-semibold text-ink-muted uppercase tracking-wide">
            Tâches
          </span>
          {tasks.length > 0 && (
            <span className="text-[10px] text-ink-faint tabular-nums">
              {doneCount}/{tasks.length}
            </span>
          )}
        </div>
      )}

      {tasks.length === 0 && readOnly && (
        <p className="text-xs text-ink-faint">Aucune tâche liée</p>
      )}

      {tasks.length > 0 && (
        <div className="space-y-0.5">
          {tasks.map((t) => {
            const isDone = t.status === "done";
            return (
              <div key={t.id} className="relative flex items-center gap-2 -mx-1 group">
                {readOnly ? (
                  <div className="flex items-center gap-2.5 flex-1 min-w-0 px-2 py-1.5">
                    <span
                      className={`shrink-0 w-4 h-4 rounded border flex items-center justify-center ${
                        isDone ? "bg-accent border-accent" : "border-border"
                      }`}
                    >
                      {isDone && (
                        <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <path d="M2 6l3 3 5-5" />
                        </svg>
                      )}
                    </span>
                    <span className={`text-xs leading-tight ${isDone ? "line-through text-ink-faint" : "text-ink"}`}>
                      {t.title}
                    </span>
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => onToggleTask(t.id)}
                      className="flex items-center gap-2.5 flex-1 min-w-0 px-2 py-1.5 rounded-lg hover:bg-surface/60 active:bg-surface/80 transition-colors text-left"
                    >
                      <span
                        className={`shrink-0 w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                          isDone
                            ? "bg-accent border-accent"
                            : "border-border hover:border-accent/50"
                        }`}
                      >
                        {isDone && (
                          <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <path d="M2 6l3 3 5-5" />
                          </svg>
                        )}
                      </span>
                      <span
                        className={`text-xs leading-tight ${
                          isDone ? "line-through text-ink-faint" : "text-ink"
                        }`}
                      >
                        {t.title}
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setMenuOpenId(menuOpenId === t.id ? null : t.id)}
                      className="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg text-ink-faint hover:text-ink hover:bg-surface/60 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
                        <circle cx="12" cy="5" r="1.5" />
                        <circle cx="12" cy="12" r="1.5" />
                        <circle cx="12" cy="19" r="1.5" />
                      </svg>
                    </button>

                    {menuOpenId === t.id && (
                      <div className="absolute right-0 top-full z-[9999] mt-0.5 bg-surface border border-border rounded-xl shadow-lg py-1 min-w-48">
                        <button
                          type="button"
                          onClick={() => { onUnlinkTask(t.id); setMenuOpenId(null); }}
                          className="w-full text-left px-4 py-2.5 text-sm font-medium text-ink hover:bg-surface-alt transition-colors"
                        >
                          Délier de l'événement
                        </button>
                        <button
                          type="button"
                          onClick={() => { onDeleteTask(t.id); setMenuOpenId(null); }}
                          className="w-full text-left px-4 py-2.5 text-sm font-medium text-danger hover:bg-danger/10 transition-colors"
                        >
                          Supprimer
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!readOnly && <form onSubmit={handleSubmit} className="flex items-center gap-2">
        <input
          type="text"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder="Nouvelle tâche…"
          className="flex-1 min-w-0 text-xs px-2.5 py-1.5 rounded-lg border border-border/50 bg-surface placeholder:text-ink-faint/50 focus:outline-none focus:ring-1 focus:ring-accent/50 focus:border-accent/30"
        />
        <button
          type="submit"
          disabled={!newTitle.trim()}
          className="shrink-0 text-xs font-medium px-3 py-1.5 rounded-lg bg-accent/10 text-accent hover:bg-accent/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          + Tâche
        </button>
      </form>}
    </div>
  );
}
